import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as NDJson from '@app/_v3/services/ndj.lib';
import { AuthService } from '@core/services/lingo2-account/auth.service';
import { DestroyableComponent } from '@models/destroyable.component';
import { EContentPanelRoute, IContentPane } from 'lingo2-models';
import { from, Observable, pipe, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ContextService } from './context.service';
import { FeaturesService } from './features.service';
import { LANGUAGE_LOCAL_STORAGE_KEY } from './language.service';

/*
 */
interface IBffPayload {
  [key: string]: any;
}

export interface IBffDefResponse {
  body: IContentPane[] | null;
  cache_key: string | null;
}

export interface IBffNdjResponse {
  body: any;
  cache_key: string | null;
}

export const BFF_ROUTES = [
  EContentPanelRoute.classes,
  EContentPanelRoute.tutors,
  EContentPanelRoute.lessons,
  EContentPanelRoute.collections,
  EContentPanelRoute.schools,
  EContentPanelRoute.main,
  '/u/',
];
export const HeaderBffDataVer = 'x-bff-data-hash';

/*
 */
@Injectable({
  providedIn: 'root',
})
export class BffService extends DestroyableComponent {
  private readonly contextService = inject(ContextService);
  private readonly authService = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly featuresService = inject(FeaturesService);

  private _TAG = `[<<<] ${this.constructor.name}:`;
  private readonly bffUrl = `${environment.bff_url}/chapter`;
  private abortController = new AbortController();

  public async abort(): Promise<void> {
    return new Promise((rv, rj) => {
      setTimeout(() => {
        this.abortController?.abort();
        this.abortController = new AbortController();
        return rv();
      }, 1);
    });
  }

  /*
   */
  public async *getContentStream(
    payload: IBffPayload,
    type: EContentPanelRoute,
  ): AsyncGenerator<{ item: any; cache_key: string | null }> {
    const url = `${this.bffUrl}/${type.toLowerCase()}`;

    try {
      const res = await NDJson.ndj_fetch(url, payload, this.abortController?.signal, this.getBaseHeaders());
      const version = res.headers.get(HeaderBffDataVer);

      for await (const item of NDJson.ndj_parse_stream(res.body, this.abortController?.signal)) {
        yield { item, cache_key: version };
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        throw err;
      }
      console.log('Fetch aborted');
    }
  }

  /*
   */
  public get_json_data(payload: IBffPayload, type: EContentPanelRoute): Observable<IBffDefResponse> {
    const url = `${this.bffUrl}/${type.toLowerCase()}`;
    return this.http
      .post<IContentPane[]>(url, payload, { observe: 'response' })
      .pipe(map((response) => ({ body: response?.body, cache_key: response?.headers?.get(HeaderBffDataVer) })));
  }

  public getBaseHeaders(): Record<string, string> {
    return {
      Authorization: 'Bearer ' + this.authService.accessToken,
      'x-portal-language': localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY),
      'x-portal-currency': this.featuresService.portalCurrency.toString(),
      'x-debug': this.contextService.debug,
    };
  }
}

/*
 */
@Injectable({ providedIn: 'root' })
export class BffCacheManager<T = any> {
  private _TAG = `[***] ${this.constructor.name}:`;
  private ttl: number = 1 * 60 * 1000; // odna minuta po umolchaniu
  private readonly dataCache: Record<string, { timestamp: number; data: T }> = {};
  private readonly versionCache: Record<string, string> = {};

  /*
   */
  constructor() {}

  public setTTL(minutes: number): void {
    this.ttl = minutes * 60 * 1000;
  }

  public get(key: string): T | null {
    const entry = this.dataCache[key];
    if (!entry) {
      return null;
    }

    const now = Date.now();
    if (now - entry.timestamp >= this.ttl) {
      this.remove(key);
      return null;
    }

    return entry.data;
  }

  public getKey(base_key: any): string {
    const path = window.location.pathname;
    const query = window.location.search;
    const finalKey = `__${base_key}_${path}${query}`;
    return finalKey;
  }

  public set(key: string, data: T, version?: string): void {
    this.dataCache[key] = { timestamp: Date.now(), data };
    if (version) {
      this.versionCache[key] = version;
    }
  }

  public getVersion(key: string): string | undefined {
    return this.versionCache[key];
  }

  public setVersion(key: string, version: string): void {
    this.versionCache[key] = version;
  }

  public hasValidVersion(key: string, version: string): boolean {
    const cached = this.get(key);
    const cachedVersion = this.versionCache[key];
    return !!cached && cachedVersion === version;
  }

  public remove(key: string): void {
    delete this.dataCache[key];
    delete this.versionCache[key];
  }

  public clear(): void {
    Object.keys(this.dataCache).forEach((key) => this.remove(key));
  }
}
